import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    DollarSign, Plus, CreditCard, TrendingUp, Receipt, Clock,
    CheckCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const Stat = ({ label, value, sub, tone = "default" }) => {
    const t = tone === "good" ? "text-emerald-300" : tone === "warn" ? "text-yellow-300" : "text-white";
    return (
        <Card className="bg-paper border-border p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">{label}</div>
            <div className={`mt-2 text-3xl font-heading font-black ${t}`}>{value}</div>
            {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
        </Card>
    );
};

const METHODS = ["mpesa", "airtel_money", "tigopesa", "card", "other"];
const PURPOSES = ["subscription", "digest", "one_off", "promo"];

const Transactions = () => {
    const { token, user } = useAuth();
    const authH = () => ({ Authorization: `Bearer ${token}` });

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState([]);
    const [trends, setTrends] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);
    const [method, setMethod] = useState("all");
    const [purpose, setPurpose] = useState("all");
    const [phoneFilter, setPhoneFilter] = useState("");
    const [profilePhone, setProfilePhone] = useState(null);

    const [showRecord, setShowRecord] = useState(false);
    const [form, setForm] = useState({ phone_number: "", amount: "", currency: "TZS", method: "mpesa", purpose: "subscription", reference: "", note: "" });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ days: String(days), limit: "200" });
            if (phoneFilter) params.set("phone", phoneFilter);
            if (method !== "all") params.set("method", method);
            if (purpose !== "all") params.set("purpose", purpose);
            const [list, tr] = await Promise.all([
                axios.get(`${API}/admin/transactions?${params.toString()}`, { headers: authH() }),
                axios.get(`${API}/admin/transactions/trends?days=${days}`, { headers: authH() }),
            ]);
            setItems(list.data.items || []);
            setTotal(list.data.total || 0);
            setTotals(list.data.totals || []);
            setTrends(tr.data);
        } catch {
            toast.error("Failed to load transactions");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [days, method, purpose]);

    const recordTransaction = async () => {
        if (!form.phone_number || !form.amount) { toast.error("Phone and amount required"); return; }
        try {
            await axios.post(`${API}/admin/transactions`, { ...form, amount: parseFloat(form.amount) }, { headers: authH() });
            toast.success("Transaction recorded");
            setShowRecord(false);
            setForm({ phone_number: "", amount: "", currency: "TZS", method: "mpesa", purpose: "subscription", reference: "", note: "" });
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Record failed");
        }
    };

    const maxDaily = (trends?.daily?.length || 0) > 0 ? Math.max(...trends.daily.map((d) => d.amount)) : 1;
    const totalAmount = totals.reduce((a, t) => a + (t.total || 0), 0);
    const totalCurrency = totals[0]?.currency || "TZS";

    const isAdmin = user?.role === "admin";

    return (
        <AdminLayout title="Transactions">
            <div className="space-y-6" data-testid="transactions-page">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-heading text-2xl font-black flex items-center gap-2">
                            <DollarSign className="h-6 w-6 text-electric" />
                            Successful transactions
                        </h2>
                        <p className="text-sm text-white/60 mt-1">
                            Every successful customer payment recorded by the telecom payment hooks (M-Pesa / Airtel Money / Tigo Pesa / card).
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <div className="flex items-center gap-1 bg-obsidian rounded-sm p-0.5">
                            {[7, 30, 90].map((d) => (
                                <button key={d} onClick={() => setDays(d)}
                                        className={`px-3 py-1 text-xs rounded-sm ${days === d ? "bg-electric text-white" : "text-white/60"}`}
                                        data-testid={`window-${d}`}>{d}d</button>
                            ))}
                        </div>
                        <Button variant="outline" onClick={fetchAll} className="border-border"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
                        {isAdmin && (
                            <Button onClick={() => setShowRecord(true)} className="bg-electric hover:bg-electric/90" data-testid="record-btn">
                                <Plus className="h-4 w-4 mr-2" /> Record
                            </Button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Revenue" value={`${totalAmount.toLocaleString()} ${totalCurrency}`} sub={`Last ${days} days`} tone="good" />
                    <Stat label="Transactions" value={total} sub="Successful only" />
                    <Stat label="Avg ticket" value={total ? `${Math.round(totalAmount / total).toLocaleString()} ${totalCurrency}` : `0 ${totalCurrency}`} />
                    <Stat label="Methods" value={trends?.by_method?.length || 0} sub={(trends?.by_method || []).slice(0, 2).map((m) => m.method).join(", ") || "—"} />
                </div>

                {trends && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-paper border-border p-4" data-testid="trend-daily">
                            <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-electric" />
                                <span className="font-heading font-bold text-xs uppercase tracking-wider">Daily revenue (last {days}d)</span>
                            </div>
                            {trends.daily.length === 0 && <div className="text-xs text-white/40">No transactions in this window.</div>}
                            {trends.daily.length > 0 && (
                                <div className="flex items-end gap-0.5 h-32">
                                    {trends.daily.map((d) => (
                                        <div key={d.date} className="flex-1 group relative">
                                            <div className="bg-electric/40 hover:bg-electric rounded-t-sm transition-colors"
                                                 style={{ height: `${Math.max(2, (d.amount / maxDaily) * 100)}%` }}
                                                 title={`${d.date}: ${d.amount} ${totalCurrency}`} />
                                            <span className="opacity-0 group-hover:opacity-100 absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-white whitespace-nowrap">{d.amount}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                        <Card className="bg-paper border-border p-4" data-testid="trend-monthly">
                            <div className="flex items-center gap-2 mb-3"><Receipt className="h-4 w-4 text-electric" />
                                <span className="font-heading font-bold text-xs uppercase tracking-wider">Monthly revenue</span>
                            </div>
                            {(trends.monthly || []).length === 0 && <div className="text-xs text-white/40">No data yet.</div>}
                            {(trends.monthly || []).length > 0 && (
                                <div className="space-y-2">
                                    {trends.monthly.map((m) => {
                                        const max = Math.max(...trends.monthly.map((x) => x.amount));
                                        const pct = max ? (m.amount / max) * 100 : 0;
                                        return (
                                            <div key={m.month}>
                                                <div className="flex justify-between text-[11px] text-white/70 mb-0.5"><span>{m.month}</span><span className="font-mono">{m.amount.toLocaleString()} {totalCurrency}</span></div>
                                                <div className="h-1.5 bg-obsidian rounded-full"><div className="h-full bg-electric" style={{ width: `${pct}%` }} /></div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    </div>
                )}

                <Card className="bg-paper border-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={method} onValueChange={setMethod}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs w-44"><SelectValue placeholder="Method" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All methods</SelectItem>
                                {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={purpose} onValueChange={setPurpose}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs w-44"><SelectValue placeholder="Purpose" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All purposes</SelectItem>
                                {PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Input placeholder="Filter by phone" value={phoneFilter}
                               onChange={(e) => setPhoneFilter(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchAll()}
                               className="bg-obsidian border-border text-white text-xs w-56" />
                        <Button size="sm" onClick={fetchAll}>Apply</Button>
                    </div>
                </Card>

                <Card className="bg-paper border-border p-0 overflow-hidden">
                    <div className="overflow-x-auto" data-testid="transactions-table">
                        <table className="w-full text-sm">
                            <thead className="bg-obsidian text-white/60 text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2 text-left">When</th>
                                    <th className="px-3 py-2 text-left">Phone</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2 text-left">Method</th>
                                    <th className="px-3 py-2 text-left">Purpose</th>
                                    <th className="px-3 py-2 text-left">Reference</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {loading && <tr><td colSpan={6} className="p-10 text-center text-white/50">Loading…</td></tr>}
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={6} className="p-10 text-center text-white/50">No transactions yet. {isAdmin && <span>Click <span className="text-electric">Record</span> to add one manually, or wait for the payment webhook to fire.</span>}</td></tr>
                                )}
                                {!loading && items.map((t) => (
                                    <tr key={t.id} className="hover:bg-subtle/40">
                                        <td className="px-3 py-2 text-xs text-white/70 flex items-center gap-1"><Clock className="h-3 w-3" />{fmt(t.created_at)}</td>
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <button onClick={() => setProfilePhone(t.phone_number)} className="text-white hover:text-electric underline-offset-2 hover:underline">{t.phone_number}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-emerald-300 text-sm">{t.amount.toLocaleString()} {t.currency}</td>
                                        <td className="px-3 py-2 text-xs"><Badge variant="outline" className="border-border text-[10px]"><CreditCard className="h-2.5 w-2.5 mr-1" />{t.method}</Badge></td>
                                        <td className="px-3 py-2 text-xs text-white/80">{t.purpose}</td>
                                        <td className="px-3 py-2 text-xs text-white/40 font-mono">{t.reference || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            <Dialog open={showRecord} onOpenChange={setShowRecord}>
                <DialogContent className="bg-paper border-border text-white" data-testid="record-dialog">
                    <DialogHeader><DialogTitle className="text-white font-heading flex items-center gap-2"><Plus className="h-5 w-5 text-electric" /> Record transaction</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div><Label className="text-white/70 text-xs">Phone</Label><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} className="bg-obsidian border-border text-white" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-white/70 text-xs">Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-obsidian border-border text-white" /></div>
                            <div><Label className="text-white/70 text-xs">Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="bg-obsidian border-border text-white" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-white/70 text-xs">Method</Label>
                                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                                    <SelectTrigger className="bg-obsidian border-border text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label className="text-white/70 text-xs">Purpose</Label>
                                <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                                    <SelectTrigger className="bg-obsidian border-border text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label className="text-white/70 text-xs">Reference (optional)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="bg-obsidian border-border text-white" placeholder="M-Pesa transaction ID" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowRecord(false)} className="text-white/60">Cancel</Button>
                        <Button onClick={recordTransaction} className="bg-electric hover:bg-electric/90" data-testid="record-submit-btn">
                            <CheckCircle className="h-4 w-4 mr-2" /> Record
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CustomerProfileSheet phone={profilePhone} open={!!profilePhone} onOpenChange={(o) => { if (!o) setProfilePhone(null); }} />
        </AdminLayout>
    );
};

export default Transactions;
